if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // OA Insert Options: replacement Buy-it-now adds cover + insert, then goes straight to checkout.
        this.oaBuyNow = this.querySelector('[data-oa-buy-now]');
        if (this.oaBuyNow) this.oaBuyNow.addEventListener('click', this.onBuyNowHandler.bind(this));
      }

      // OA 9-14-26: the cover line no longer carries an "Insert: ..." property. The insert is its own
      // line item, so the note was redundant and survived if the customer removed the insert from the
      // cart, making order emails and the admin order look like an insert was purchased when it wasn't.

      onBuyNowHandler(evt) {
        evt.preventDefault();
        const insertVariantId = this.dataset.oaInsertVariant;
        if (!insertVariantId) { this.form.requestSubmit(); return; }
        if (this.oaBuyNow.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.oaBuyNow.setAttribute('aria-disabled', true);
        this.oaBuyNow.classList.add('loading');
        const spinner = this.oaBuyNow.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        const formData = new FormData(this.form);
        const quantity = parseInt(formData.get('quantity'), 10) || 1;
        const coverItem = { id: formData.get('id'), quantity: quantity };
        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) coverItem.selling_plan = sellingPlan;

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.headers['Content-Type'] = 'application/json';
        config.body = JSON.stringify({ items: [coverItem, { id: insertVariantId, quantity: quantity }] });

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.handleErrorMessage(response.description || response.message);
              this.oaBuyNow.removeAttribute('aria-disabled');
              this.oaBuyNow.classList.remove('loading');
              if (spinner) spinner.classList.add('hidden');
              return;
            }
            window.location.href = "/checkout";
          })
          .catch((e) => {
            console.error(e);
            this.oaBuyNow.removeAttribute('aria-disabled');
            this.oaBuyNow.classList.remove('loading');
            if (spinner) spinner.classList.add('hidden');
          });
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        const formData = new FormData(this.form);
        const sectionIds = this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : null;
        if (this.cart) this.cart.setActiveElement(document.activeElement);

        // OA Insert Options: when an insert is selected, add cover + matching-size insert together.
        const insertVariantId = this.dataset.oaInsertVariant;
        if (insertVariantId) {
          const quantity = parseInt(formData.get('quantity'), 10) || 1;
          const coverItem = { id: formData.get('id'), quantity: quantity };
          const sellingPlan = formData.get('selling_plan');
          if (sellingPlan) coverItem.selling_plan = sellingPlan;
          const payload = { items: [coverItem, { id: insertVariantId, quantity: quantity }] };
          if (sectionIds) {
            payload.sections = sectionIds.join(',');
            payload.sections_url = window.location.pathname;
          }
          config.headers['Content-Type'] = 'application/json';
          config.body = JSON.stringify(payload);
        } else {
          delete config.headers['Content-Type'];
          if (sectionIds) {
            formData.append('sections', sectionIds);
            formData.append('sections_url', window.location.pathname);
          }
          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            // Multi-item add returns { items: [...], sections } — normalise to the single-item shape the cart UI expects.
            if (!response.status && Array.isArray(response.items) && response.items.length) {
              response = Object.assign({}, response.items[0], { sections: response.sections });
            }
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
            });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener('modalClosed', () => {
                setTimeout(() => { this.cart.renderContents(response) });
              }, { once: true });
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');

          if (this.submitButton.classList.contains('button--preorder')) {
            this.submitButtonText.textContent = window.variantStrings.preOrder;
          } else {
            this.submitButtonText.textContent = window.variantStrings.addToCart;
          }
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
